import './settings-leaf.css';
import { useTranslation } from 'react-i18next';
import { Checkbox, Select } from '../ui.tsx';
import { NumberInput } from '../number-input.tsx';
import { CardSaveFooter, useCompanyCardForm } from './company-card-form.tsx';
import type { Company } from '@shared/types/index.ts';
import { CANCELLED_SLOT_AUDIENCES, type CancelledSlotAudience } from '@shared/cancelled-slots.ts';
import { Link } from 'react-router-dom';
import { usePlanFeature } from '../plan-gate.tsx';
import { useSession } from '../../state/session.tsx';

/**
 * Offer a canceled visit's time to customers who asked to come earlier.
 * Off until switched on. Every paid plan (Starter, Pro, Business), never
 * Free: on Free the card stays visible but locked, with one line saying
 * which plans carry it. The Worker refuses turning it on regardless.
 */

const snapshot = (company: Company) => ({
  autoOfferCancelledSlots: company.autoOfferCancelledSlots,
  cancelledSlotNoticeHours: company.cancelledSlotNoticeHours,
  cancelledSlotRoundHours: company.cancelledSlotRoundHours,
  cancelledSlotRoundSize: company.cancelledSlotRoundSize,
  cancelledSlotQuietStartHour: company.cancelledSlotQuietStartHour,
  cancelledSlotQuietEndHour: company.cancelledSlotQuietEndHour,
  cancelledSlotTeamScope: company.cancelledSlotTeamScope ?? 'same_team',
  cancelledSlotRequireApproval: company.cancelledSlotRequireApproval,
  cancelledSlotOfferAudience: company.cancelledSlotOfferAudience,
});

const toPayload = (draft: ReturnType<typeof snapshot>) => ({
  autoOfferCancelledSlots: draft.autoOfferCancelledSlots,
  cancelledSlotNoticeHours: draft.cancelledSlotNoticeHours,
  cancelledSlotRoundHours: draft.cancelledSlotRoundHours,
  cancelledSlotRoundSize: draft.cancelledSlotRoundSize,
  cancelledSlotQuietStartHour: draft.cancelledSlotQuietStartHour,
  cancelledSlotQuietEndHour: draft.cancelledSlotQuietEndHour,
  cancelledSlotTeamScope: draft.cancelledSlotTeamScope,
  cancelledSlotRequireApproval: draft.cancelledSlotRequireApproval,
  cancelledSlotOfferAudience: draft.cancelledSlotOfferAudience,
});

export default function FillSlotsCard() {
  const { t } = useTranslation();
  const form = useCompanyCardForm(snapshot, toPayload);
  const allowed = usePlanFeature('earlier_visit_offers');
  const { user } = useSession();
  const draft = form.draft;
  if (!draft) return null;
  const locked = form.readOnly || !allowed;

  return (
    <section className="card work-section settings-leaf mb-6" aria-labelledby="fill-slots-heading">
      <div className="work-section-heading">
        <h2 id="fill-slots-heading" className="mb-4 text-lg font-semibold">
          {t('settings.fillSlotsHeading')}
        </h2>
      </div>
      <p className="mb-4 text-sm text-[color:var(--color-muted)]">{t('settings.fillSlotsDescription')}</p>
      {!allowed ? (
        <p className="mb-4 text-sm" data-testid="fill-slots-locked">
          {t('settings.fillSlotsLocked')}{' '}
          {user?.role === 'owner' ? (
            <Link to="/settings#settings-billing" className="font-semibold text-[color:var(--color-brand)] underline">
              {t('settings.fillSlotsSeePlans')}
            </Link>
          ) : null}
        </p>
      ) : null}
      <Checkbox
        label={t('settings.fillSlotsEnable')}
        hint={t('settings.fillSlotsEnableHint')}
        checked={allowed && draft.autoOfferCancelledSlots}
        disabled={locked}
        onChange={(event) => form.set('autoOfferCancelledSlots', event.target.checked)}
      />
      <Select
        label={t('savedTeams.offerAudience')}
        hint={t('savedTeams.offerAudienceHint')}
        value={draft.cancelledSlotTeamScope}
        disabled={locked}
        onChange={(event) => form.set('cancelledSlotTeamScope', event.target.value as 'same_team' | 'any_eligible')}
        options={[
          { value: 'same_team', label: t('savedTeams.sameTeam') },
          { value: 'any_eligible', label: t('savedTeams.anyClient') },
        ]}
      />
      <Checkbox
        label={t('settings.fillSlotsApproval')}
        hint={t('settings.fillSlotsApprovalHint')}
        checked={allowed && draft.cancelledSlotRequireApproval}
        disabled={locked}
        onChange={(event) => form.set('cancelledSlotRequireApproval', event.target.checked)}
      />
      <Select
        label={t('settings.fillSlotsAudience')}
        hint={t('settings.fillSlotsAudienceHint')}
        value={draft.cancelledSlotOfferAudience}
        disabled={locked}
        error={form.fieldErrors.cancelledSlotOfferAudience}
        options={CANCELLED_SLOT_AUDIENCES.map((audience) => ({
          value: audience,
          label: t(audience === 'same_team' ? 'settings.fillSlotsAudienceSameTeam' : 'settings.fillSlotsAudienceAny'),
        }))}
        onChange={(event) => form.set('cancelledSlotOfferAudience', event.target.value as CancelledSlotAudience)}
      />
      <div className="grid gap-x-4 sm:grid-cols-2">
        <NumberInput
          label={t('settings.fillSlotsNoticeHours')}
          min={1}
          max={336}
          value={draft.cancelledSlotNoticeHours}
          disabled={locked}
          error={form.fieldErrors.cancelledSlotNoticeHours}
          onValueChange={(hours) => form.set('cancelledSlotNoticeHours', hours ?? 12)}
        />
        <NumberInput
          label={t('settings.fillSlotsRoundSize')}
          hint={t('settings.fillSlotsRoundSizeHint')}
          min={1}
          max={20}
          value={draft.cancelledSlotRoundSize}
          disabled={locked}
          error={form.fieldErrors.cancelledSlotRoundSize}
          onValueChange={(count) => form.set('cancelledSlotRoundSize', count ?? 5)}
        />
        <NumberInput
          label={t('settings.fillSlotsRoundHours')}
          hint={t('settings.fillSlotsRoundHoursHint')}
          min={1}
          max={72}
          value={draft.cancelledSlotRoundHours}
          disabled={locked}
          error={form.fieldErrors.cancelledSlotRoundHours}
          onValueChange={(hours) => form.set('cancelledSlotRoundHours', hours ?? 2)}
        />
        <NumberInput
          label={t('settings.fillSlotsQuietStart')}
          min={0}
          max={23}
          value={draft.cancelledSlotQuietStartHour}
          disabled={locked}
          error={form.fieldErrors.cancelledSlotQuietStartHour}
          onValueChange={(hour) => form.set('cancelledSlotQuietStartHour', hour ?? 21)}
        />
        <NumberInput
          label={t('settings.fillSlotsQuietEnd')}
          hint={t('settings.fillSlotsQuietHint')}
          min={0}
          max={23}
          value={draft.cancelledSlotQuietEndHour}
          disabled={locked}
          error={form.fieldErrors.cancelledSlotQuietEndHour}
          onValueChange={(hour) => form.set('cancelledSlotQuietEndHour', hour ?? 8)}
        />
      </div>
      {allowed ? <CardSaveFooter form={form} saveLabel={t('settings.saveFillSlots')} /> : null}
    </section>
  );
}
